import React, {FC, useState} from "react";
import {Alert, Button, Card, message, Modal, Space, Table, Tag} from "antd";
import moment from "moment";
import Roles from "../../../../../shared/models/roles";
import {
    Tarif595ReceiptStatus,
    useCancelTarif595ReceiptMutation,
    useGetTarif595ReceiptPdfLazyQuery,
    useGetTarif595ReceiptsQuery,
    useGetTarif595ReceiptXmlLazyQuery,
    useIssueTarif595ReceiptMutation,
    useSendTarif595ReceiptMutation,
} from "../../../../generated/graphql";
import i18n from "../../../../services/i18n";
import {useHasRoleAtLeast} from "../../../../helpers/useIsStaffOnlyAccess";
import {useTarif595Enabled} from "../../../../hooks/useTarif595Enabled";

// Getter statt Modul-Konstante (react-hot-loader, siehe BookingDunningsPanel)
const tr = () => i18n.containers.invoice.BookingTarif595Receipts;

const formatDate = (date: string) => moment(date).format("DD.MM.YYYY");

const errorText = (error: unknown) => (error instanceof Error ? error.message.replace(/^GraphQL error: /, "") : String(error));

/** Tarif 595: Rückforderungsbeleg der Buchung ausstellen, als PDF oder XML abrufen, per E-Mail senden, stornieren. Nur mit Flag, CHF und ab Rolle Admin. */
const BookingTarif595Panel: FC<{bookingId: string}> = ({bookingId}) => {
    const enabled = useTarif595Enabled();
    const isAdmin = useHasRoleAtLeast(Roles.Admin);

    return enabled && isAdmin ? <ReceiptsCard bookingId={bookingId} /> : null;
};

const ReceiptsCard: FC<{bookingId: string}> = ({bookingId}) => {
    const {data, refetch} = useGetTarif595ReceiptsQuery({variables: {bookingId}, fetchPolicy: "network-only"});
    const [issue, {loading: issuing}] = useIssueTarif595ReceiptMutation();
    const [cancel] = useCancelTarif595ReceiptMutation();
    const [send] = useSendTarif595ReceiptMutation();
    // XML und PDF enthalten die AHV-Nummer: nicht im Apollo-Cache halten
    const [loadXml] = useGetTarif595ReceiptXmlLazyQuery({fetchPolicy: "no-cache"});
    const [loadPdf] = useGetTarif595ReceiptPdfLazyQuery({fetchPolicy: "no-cache"});
    const [downloading, setDownloading] = useState<string>();

    const receipts = data?.tarif595Receipts ?? [];
    const missing = data?.tarif595ReceiptMissing ?? [];
    const hasValid = receipts.some((receipt) => receipt.status === Tarif595ReceiptStatus.Issued);

    const onIssue = async () => {
        try {
            const result = await issue({variables: {bookingId}});

            message.success(tr().issued({number: `RB-${result.data?.issueTarif595Receipt.receiptNumber}`}));
        } catch (error) {
            message.error(errorText(error), 8);
        }
        await refetch();
    };

    const loadFile = async (receiptId: string, kind: "pdf" | "xml"): Promise<Blob> => {
        if (kind === "xml") {
            const result = await loadXml({variables: {receiptId}});

            if (!result.data?.tarif595ReceiptXml) {
                throw result.error ?? new Error("XML fehlt");
            }

            return new Blob([result.data.tarif595ReceiptXml], {type: "application/xml"});
        }
        const result = await loadPdf({variables: {receiptId}});

        if (!result.data?.tarif595ReceiptPdf) {
            throw result.error ?? new Error("PDF fehlt");
        }

        return new Blob([Uint8Array.from(atob(result.data.tarif595ReceiptPdf), (char) => char.charCodeAt(0))], {type: "application/pdf"});
    };

    const onDownload = async (receiptId: string, number: string, kind: "pdf" | "xml") => {
        setDownloading(`${receiptId}-${kind}`);
        try {
            const link = document.createElement("a");

            link.href = URL.createObjectURL(await loadFile(receiptId, kind));
            link.download = `${number}.${kind}`;
            link.click();
            URL.revokeObjectURL(link.href);
        } catch (error) {
            message.error(errorText(error), 8);
        } finally {
            setDownloading(undefined);
        }
    };

    const onSend = (receiptId: string, number: string, sentAt?: string | null) =>
        Modal.confirm({
            title: tr().sendConfirmTitle({number}),
            content: (
                <>
                    <p>{tr().sendConfirmText()}</p>
                    {sentAt && <p>{tr().sendConfirmAgain({date: formatDate(sentAt)})}</p>}
                </>
            ),
            okText: tr().send(),
            onOk: async () => {
                try {
                    await send({variables: {receiptId}});
                    message.success(tr().sent({number}));
                } catch (error) {
                    message.error(errorText(error), 8);
                }
                await refetch();
            },
        });

    const onCancel = (receiptId: string, number: string) =>
        Modal.confirm({
            title: tr().cancelConfirmTitle({number}),
            content: tr().cancelConfirmText(),
            okText: tr().cancel(),
            okButtonProps: {danger: true},
            onOk: async () => {
                try {
                    await cancel({variables: {receiptId}});
                    message.success(tr().cancelled({number}));
                } catch (error) {
                    message.error(errorText(error), 8);
                }
                await refetch();
            },
        });

    return (
        <Card title={tr().title()} size="small" style={{marginTop: 16}} data-testid="BookingTarif595Panel">
            <p>{tr().intro()}</p>
            {!hasValid && missing.length > 0 && (
                <Alert
                    type="warning"
                    showIcon
                    style={{marginBottom: 12}}
                    message={tr().missingTitle()}
                    description={
                        <ul style={{margin: 0, paddingLeft: 18}}>
                            {missing.map((item) => (
                                <li key={item}>{item}</li>
                            ))}
                        </ul>
                    }
                />
            )}
            {!hasValid && (
                <Button type="primary" disabled={missing.length > 0} loading={issuing} onClick={onIssue} style={{marginBottom: 12}} data-testid="Tarif595IssueButton">
                    {tr().issue()}
                </Button>
            )}
            {receipts.length === 0 ? (
                <div style={{color: "rgba(0, 0, 0, 0.45)"}}>{tr().none()}</div>
            ) : (
                <Table
                    size="small"
                    rowKey="id"
                    pagination={false}
                    dataSource={receipts}
                    columns={[
                        {title: tr().columns.number(), render: (_, receipt) => `RB-${receipt.receiptNumber}`},
                        {
                            title: tr().columns.status(),
                            render: (_, receipt) =>
                                receipt.status === Tarif595ReceiptStatus.Issued ? <Tag color="green">{tr().statusIssued()}</Tag> : <Tag>{tr().statusCancelled()}</Tag>,
                        },
                        {title: tr().columns.period(), render: (_, receipt) => `${formatDate(receipt.serviceDateBegin)} – ${formatDate(receipt.serviceDateEnd)}`},
                        {title: tr().columns.tariffCode(), dataIndex: "tariffCode"},
                        {title: tr().columns.amount(), render: (_, receipt) => `CHF ${Number(receipt.totalAmount).toFixed(2)}`},
                        {title: tr().columns.issuedAt(), render: (_, receipt) => formatDate(receipt.createDate)},
                        {title: tr().columns.sentAt(), render: (_, receipt) => (receipt.sentAt ? formatDate(receipt.sentAt) : "–")},
                        {
                            key: "actions",
                            render: (_, receipt) =>
                                receipt.status === Tarif595ReceiptStatus.Issued && (
                                    <Space>
                                        <Button size="small" loading={downloading === `${receipt.id}-pdf`} onClick={() => onDownload(receipt.id, `RB-${receipt.receiptNumber}`, "pdf")}>
                                            {tr().downloadPdf()}
                                        </Button>
                                        <Button size="small" loading={downloading === `${receipt.id}-xml`} onClick={() => onDownload(receipt.id, `RB-${receipt.receiptNumber}`, "xml")}>
                                            {tr().downloadXml()}
                                        </Button>
                                        <Button size="small" onClick={() => onSend(receipt.id, `RB-${receipt.receiptNumber}`, receipt.sentAt)}>
                                            {tr().send()}
                                        </Button>
                                        <Button size="small" danger onClick={() => onCancel(receipt.id, `RB-${receipt.receiptNumber}`)}>
                                            {tr().cancel()}
                                        </Button>
                                    </Space>
                                ),
                        },
                    ]}
                />
            )}
        </Card>
    );
};

export default BookingTarif595Panel;
